import { useSearchParams } from "react-router-dom";

import { DateStringToZeroDate } from "@/utils/core/generalUtils";
import {
  appendSanitizedQueryString,
  areValidBrowserDateRangeValues,
  sanitizeBrowserQueryParams,
} from "@/utils/form/queryParams";

interface BuildQueryStringOptions {
  forceDefaultKeys?: readonly string[];
}

const shouldTrimQueryValue = (key: string): boolean => {
  return key.toLowerCase().includes("search");
};

const resolveQueryValue = (
  key: string,
  queryValue: string | undefined | null,
): string => {
  const safeValue = queryValue ?? "";
  return shouldTrimQueryValue(key) ? safeValue.trim() : safeValue;
};

/**
 * Builds the API filter query string from browser search params.
 *
 * @param {URLSearchParams} searchParams - Browser search params.
 * @param {string[]} keys - Query keys to forward.
 * @param {Record<string, string>} defaults - Default values for missing keys.
 * @param {BuildQueryStringOptions} options - Query composition options.
 * @returns {string} API-ready query string.
 */
export const buildQueryStringFromSearchParams = (
  searchParams: URLSearchParams,
  keys: string[],
  defaults: Record<string, string> = {},
  options: BuildQueryStringOptions = {},
): string => {
  const browserParams = sanitizeBrowserQueryParams(searchParams);
  const queryParams = new URLSearchParams();
  const forceDefaultKeys = new Set(options.forceDefaultKeys ?? []);

  Object.entries(defaults).forEach(([key, defaultValue]) => {
    if (forceDefaultKeys.has(key) || !browserParams.get(key)) {
      queryParams.set(key, resolveQueryValue(key, defaultValue));
    }
  });

  keys.forEach((key) => {
    if (forceDefaultKeys.has(key)) {
      return;
    }

    if (key === "fortnight" || key === "dateRange") {
      const dateRangeValues = browserParams.getAll(key);

      if (
        dateRangeValues.length >= 2 &&
        areValidBrowserDateRangeValues(key, dateRangeValues)
      ) {
        const startDate = DateStringToZeroDate(dateRangeValues[0]);
        const endDate = DateStringToZeroDate(dateRangeValues[1]);

        queryParams.set("startDate", startDate);
        queryParams.set("endDate", endDate);
      }

      return;
    }

    const queryValues = browserParams.getAll(key);
    if (queryValues.length > 0) {
      queryValues.forEach((queryValue) => {
        const resolvedQueryValue = resolveQueryValue(key, queryValue);

        if (resolvedQueryValue) {
          queryParams.append(key, resolvedQueryValue);
        }
      });
    }
  });

  const sanitizedParams = new URLSearchParams();
  appendSanitizedQueryString(sanitizedParams, queryParams.toString());

  return sanitizedParams.toString() ? `?${sanitizedParams.toString()}` : "";
};

/**
 * Reads selected URL params and maps date ranges to backend filter keys.
 *
 * @param {string[]} keys - Query keys to forward.
 * @param {Record<string, string>} defaults - Default values for missing keys.
 * @param {BuildQueryStringOptions} options - Query composition options.
 * @returns {string} API-ready query string.
 */
export const useGetParams = (
  keys: string[],
  defaults: Record<string, string> = {},
  options: BuildQueryStringOptions = {},
): string => {
  const [searchParams] = useSearchParams();

  return buildQueryStringFromSearchParams(
    searchParams,
    keys,
    defaults,
    options,
  );
};
